import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Typography,
  Space,
  Form,
  Select,
  Checkbox,
  Row,
  Col,
  Alert,
  Progress,
  Table,
  Tag,
  Modal,
  Descriptions
} from 'antd';
import {
  ArrowLeftOutlined,
  FileTextOutlined,
  FileExcelOutlined,
  DownloadOutlined,
  EyeOutlined,
  PlusOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  sections: string[];
}

interface GeneratedReport {
  report_id: string;
  study_id: string;
  report_type: string;
  format: string;
  file_size_bytes: number;
  generated_at: string;
  generated_by: string;
}

const InsightGenerator: React.FC = () => {
  const { studyId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [existingReports, setExistingReports] = useState<GeneratedReport[]>([]);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewContent, setPreviewContent] = useState<any>(null);

  useEffect(() => {
    loadTemplates();
    loadExistingReports();
  }, [studyId]);

  const loadTemplates = async () => {
    const mockTemplates: ReportTemplate[] = [
      {
        id: 'summary',
        name: 'Executive Summary',
        description: 'High-level findings and key insights',
        sections: ['methodology', 'key_findings', 'recommendations']
      },
      {
        id: 'detailed',
        name: 'Detailed Analysis',
        description: 'Comprehensive analysis with all data',
        sections: ['methodology', 'data_overview', 'detailed_findings', 'analysis', 'recommendations', 'appendix']
      },
      {
        id: 'thematic',
        name: 'Thematic Analysis',
        description: 'Focus on themes and narrative elements',
        sections: ['methodology', 'theme_analysis', 'character_analysis', 'narrative_patterns']
      },
      {
        id: 'representation',
        name: 'Representation Analysis',
        description: 'Focus on demographic representation',
        sections: ['methodology', 'demographic_breakdown', 'representation_gaps', 'recommendations']
      }
    ];
    setTemplates(mockTemplates);
  };

  const loadExistingReports = async () => {
    const mockReports: GeneratedReport[] = [
      {
        report_id: '1',
        study_id: studyId || '',
        report_type: 'summary',
        format: 'docx',
        file_size_bytes: 1024000,
        generated_at: '2024-01-15T10:30:00Z',
        generated_by: 'researcher@nrg.com'
      },
      {
        report_id: '2',
        study_id: studyId || '',
        report_type: 'detailed',
        format: 'both',
        file_size_bytes: 2048000,
        generated_at: '2024-01-14T15:45:00Z',
        generated_by: 'analyst@nrg.com'
      }
    ];
    setExistingReports(mockReports);
  };

  const handlePreview = async (values: any) => {
    try {
      // Mock preview content
      const mockPreview = {
        title: `Preview - ${templates.find(t => t.id === values.report_type)?.name}`,
        sections_to_include: values.include_sections,
        estimated_length: values.include_sections?.length * 2 || 0,
        data_points: {
          total_annotations: 156,
          flagged_insights: 12,
          media_files: 45
        }
      };
      
      setPreviewContent(mockPreview);
      setPreviewModalVisible(true);
    } catch (error) {
      console.error('Failed to generate preview:', error);
    }
  };

  const handleGenerate = async (values: any) => {
    setGenerating(true);
    setProgress(0);

    // Simulate report generation progress
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setGenerating(false);
          
          // Add new report to existing reports
          const newReport: GeneratedReport = {
            report_id: Date.now().toString(),
            study_id: studyId || '',
            report_type: values.report_type,
            format: values.format,
            file_size_bytes: Math.floor(Math.random() * 2000000) + 500000,
            generated_at: new Date().toISOString(),
            generated_by: 'current_user@nrg.com'
          };
          
          setExistingReports(prev => [newReport, ...prev]);
          
          return 100;
        }
        return prev + 10;
      });
    }, 300);
  };

  const downloadReport = (reportId: string, format: string) => {
    // Mock download - in real app, this would be an API call
    const link = document.createElement('a');
    link.href = `/api/v1/reports/${reportId}/download?format=${format}`;
    link.download = `study_report_${reportId}.${format}`;
    link.click();
  };

  const formatFileSize = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const reportColumns = [
    {
      title: 'Report Type',
      dataIndex: 'report_type',
      key: 'report_type',
      render: (type: string) => (
        <Tag color="purple">
          {templates.find(t => t.id === type)?.name || type.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Format',
      dataIndex: 'format',
      key: 'format',
      render: (format: string) => (
        <Space>
          {(format === 'docx' || format === 'both') && (
            <Tag color="blue" icon={<FileTextOutlined />}>DOCX</Tag>
          )}
          {(format === 'xlsx' || format === 'both') && (
            <Tag color="green" icon={<FileExcelOutlined />}>XLSX</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'file_size_bytes',
      key: 'file_size_bytes',
      render: formatFileSize,
    },
    {
      title: 'Generated',
      dataIndex: 'generated_at',
      key: 'generated_at',
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: GeneratedReport) => (
        <Space size="small">
          {(record.format === 'docx' || record.format === 'both') && (
            <Button 
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadReport(record.report_id, 'docx')}
            >
              Word
            </Button>
          )}
          {(record.format === 'xlsx' || record.format === 'both') && (
            <Button 
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadReport(record.report_id, 'xlsx')}
            >
              Excel
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Space>
        <Title level={2} style={{ marginTop: 16 }}>
          Insight Generator
        </Title>
        <Paragraph>
          Generate professional reports for Study: {studyId}
        </Paragraph>
      </div>

      <Row gutter={[16, 16]}>
        {/* Report Generation Form */}
        <Col xs={24} lg={12}>
          <Card title="Generate New Report">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleGenerate}
              initialValues={{
                include_flagged_insights: true,
                include_annotations: true,
                format: 'docx'
              }}
            >
              <Form.Item
                name="report_type"
                label="Report Template"
                rules={[{ required: true, message: 'Please select a report template' }]}
              >
                <Select placeholder="Select template">
                  {templates.map(template => (
                    <Select.Option key={template.id} value={template.id}>
                      <div>
                        <Text strong>{template.name}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {template.description}
                        </Text>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="include_sections"
                label="Report Sections"
              >
                <Checkbox.Group>
                  <Row>
                    <Col span={12}><Checkbox value="methodology">Methodology</Checkbox></Col>
                    <Col span={12}><Checkbox value="findings">Key Findings</Checkbox></Col>
                    <Col span={12}><Checkbox value="analysis">Analysis</Checkbox></Col>
                    <Col span={12}><Checkbox value="recommendations">Recommendations</Checkbox></Col>
                    <Col span={12}><Checkbox value="appendix">Appendix</Checkbox></Col>
                  </Row>
                </Checkbox.Group>
              </Form.Item>

              <Form.Item
                name="format"
                label="Output Format"
                rules={[{ required: true, message: 'Please select output format' }]}
              >
                <Select>
                  <Select.Option value="docx">Word Document (.docx)</Select.Option>
                  <Select.Option value="xlsx">Excel Workbook (.xlsx)</Select.Option>
                  <Select.Option value="both">Both Formats</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item name="include_flagged_insights" valuePropName="checked">
                <Checkbox>Include flagged insights from chat sessions</Checkbox>
              </Form.Item>

              <Form.Item name="include_annotations" valuePropName="checked">
                <Checkbox>Include human-edited annotations</Checkbox>
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button 
                    type="default" 
                    icon={<EyeOutlined />}
                    onClick={() => form.validateFields().then(handlePreview)}
                  >
                    Preview
                  </Button>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    loading={generating}
                    icon={<PlusOutlined />}
                  >
                    Generate Report
                  </Button>
                </Space>
              </Form.Item>
            </Form>

            {generating && (
              <Alert
                message="Generating Report"
                description="AI is compiling your report. This may take a few minutes."
                type="info"
                style={{ marginTop: 16 }}
              />
            )}

            {generating && (
              <Progress 
                percent={progress} 
                status="active"
                style={{ marginTop: 16 }}
              />
            )}
          </Card>
        </Col>

        {/* Template Details */}
        <Col xs={24} lg={12}>
          <Card title="Template Information">
            <Paragraph>
              <Text strong>Available Templates:</Text>
            </Paragraph>
            
            {templates.map(template => (
              <Card 
                key={template.id}
                size="small" 
                style={{ marginBottom: 12 }}
                title={template.name}
              >
                <Paragraph style={{ marginBottom: 8 }}>
                  {template.description}
                </Paragraph>
                <Text type="secondary">
                  Sections: {template.sections.join(', ')}
                </Text>
              </Card>
            ))}
          </Card>
        </Col>
      </Row>

      {/* Existing Reports */}
      <Card 
        title="Generated Reports" 
        style={{ marginTop: 24 }}
        extra={<Text type="secondary">{existingReports.length} reports</Text>}
      >
        <Table
          columns={reportColumns}
          dataSource={existingReports}
          rowKey="report_id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Preview Modal */}
      <Modal
        title="Report Preview"
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            Close
          </Button>,
          <Button 
            key="generate" 
            type="primary"
            onClick={() => {
              setPreviewModalVisible(false);
              form.submit();
            }}
          >
            Generate Report
          </Button>
        ]}
        width={600}
      >
        {previewContent && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Title">
              {previewContent.title}
            </Descriptions.Item>
            <Descriptions.Item label="Sections">
              {previewContent.sections_to_include?.join(', ') || 'All sections'}
            </Descriptions.Item>
            <Descriptions.Item label="Estimated Length">
              {previewContent.estimated_length} pages
            </Descriptions.Item>
            <Descriptions.Item label="Data Points">
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Annotations: {previewContent.data_points.total_annotations}</li>
                <li>Flagged Insights: {previewContent.data_points.flagged_insights}</li>
                <li>Media Files: {previewContent.data_points.media_files}</li>
              </ul>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default InsightGenerator; 